import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ReactNode, useState } from "react";
import { useParams } from "react-router-dom";

import { AI_GENERATED, Button, Folder, FolderExplorer, UserType } from "@fronterahealth/frontera-ui-components";

import {
  CreateRecommendationBankItemInput,
  GetRecommendationBankItemsQuery,
  ReportSectionFieldTypeEnums,
  useCreateReportSectionFieldMutation,
  useGetRecommendationBankItemsQuery,
} from "@api/graphql/types-and-hooks";
import { FolderManagementAction } from "@components/GoalsBank/constants";
import { DeleteFolderDialog } from "@components/GoalsBank/dialogs/DeleteFolderDialog";
import { FolderManagementDialog } from "@components/GoalsBank/dialogs/FolderManagementDialog";
import { getFolderSelections } from "@components/GoalsBank/utils";
import { RecommendationBankCard } from "@components/RecommendationsBank/RecommendationBankCard";
import { RecommendationsBankPanel } from "@components/RecommendationsBank/RecommendationsBankPanel";
import { useRecommendationsBankFolders } from "@components/RecommendationsBank/hooks/useRecommendationsBankFolders";
import {
  SelectedRecommendations,
  getSelectAllVisibleProps,
  getVisibleRecommendations,
  toggleRecommendation,
} from "@components/RecommendationsBank/selectAllVisible";
import { findFolder, getOrgFolderSection, getUserFolderSection } from "@components/RecommendationsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

type BankRecommendation = NonNullable<GetRecommendationBankItemsQuery["getRecommendationBankItems"]>[number];

interface AddRecommendationsBankProps {
  onAdd: () => Promise<void>;
  sectionId: string;
  newRankOrder: number;
}

export const AddRecommendationsBank: React.FC<AddRecommendationsBankProps> = ({ onAdd, sectionId, newRankOrder }) => {
  const { evaluationId: reportId } = useParams();
  const { user } = useAuth0();
  const userType: UserType = !!user?.org_id ? "organization" : "selfServe";
  const [searchTerm, setSearchTerm] = useState<string>("");
  const orgName = `${user![config.auth0Audience + "/orgName"] ?? ""}`;
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [isSaveToFolderDialogOpen, setSaveToFolderDialogOpen] = useState<boolean>(false);
  const [selectedRecommendations, setSelectedRecommendations] = useState<SelectedRecommendations<BankRecommendation>>(
    {},
  );
  const [selectedAiRecommendationIds, setSelectedAiRecommendationIds] = useState<string[]>([]);
  const { userFolders, orgFolders, createNewFolder, deleteFolder, editFolder } =
    useRecommendationsBankFolders(searchTerm);

  const addReportSectionFieldMutation = useCreateReportSectionFieldMutation({});

  const recommendationBankQuery = useGetRecommendationBankItemsQuery({ searchTerm }, { refetchOnMount: "always" });

  const recommendationsFromBank = recommendationBankQuery.data?.getRecommendationBankItems || [];

  const onSelectedFolder = (folder: Folder) => {
    setSelectedFolderId(folder.id);
  };

  const userFoldersSection = getUserFolderSection(userFolders);
  const orgFoldersSection = getOrgFolderSection(orgFolders, orgName);

  const [deleteFolderId, setDeleteFolderId] = useState<string>("");
  const [openDeleteFolderDialog, setDeleteFolderDialogOpen] = useState(false);

  const visibleRecommendations = getVisibleRecommendations(recommendationsFromBank, selectedFolderId);
  const selection = getSelectAllVisibleProps({
    selectedFolderId,
    visibleRecommendations,
    selected: selectedRecommendations,
    setSelected: setSelectedRecommendations,
  });

  const recommendationsToAdd = (): ReactNode[] => {
    if (selectedFolderId === AI_GENERATED) {
      return [<></>];
    }
    return (
      visibleRecommendations.map((rec) => {
        return rec ? (
          <RecommendationBankCard
            id={rec.id}
            key={rec.id}
            title={rec.recommendationTitle || "<missing-title>"}
            body={rec.recommendationContent || "<missing-content>"}
            selected={!!selectedRecommendations[rec.id]}
            onClick={() => setSelectedRecommendations((prev) => toggleRecommendation(prev, rec))}
          />
        ) : (
          <></>
        );
      }) || []
    );
  };

  const addRecommendations = async () => {
    if (selectedRecommendations || selectedAiRecommendationIds) {
      try {
        if (selectedRecommendations) {
          for (const [index, rec] of Object.values(selectedRecommendations).entries()) {
            await addReportSectionFieldMutation.mutateAsync({
              reportId: reportId || "<missing-reportId>",
              reportSectionId: sectionId,
              fieldData: {
                content: rec.recommendationContent,
                title: rec.recommendationTitle || "<missing-recommendation-title>",
                fieldType: ReportSectionFieldTypeEnums.Wysiwyg,
                rankOrder: newRankOrder + index,
                replacePlaceholders: true,
              },
            });
          }
          await onAdd();
        }
      } catch (err) {
        notifyError("Failed To Add Recommendation(s)");
        console.error("Error adding AI recommendations", err);
      } finally {
        setSelectedAiRecommendationIds([]);
        setSelectedRecommendations({});
        notifySuccess("Successfully Added Recommendation(s)");
      }
    }
  };

  const { formState, RegisteredFormSelected, reset, onSubmit } = useFormUtils<CreateRecommendationBankItemInput>({
    mutationFn: async (params) => {
      if (params.folderId) {
        //create recommendations mutation goes here
      }
    },
  });

  const showDeleteFolderDialog = (folderId: string) => {
    setDeleteFolderId(folderId);
    setDeleteFolderDialogOpen(true);
  };

  return (
    <>
      <FolderExplorer
        userType={userType}
        selectedFolderId={selectedFolderId}
        onDeleteFolder={(folderId) => {
          showDeleteFolderDialog(folderId);
        }}
        onEditFolder={(id, newValue) => editFolder(findFolder(id, userFolders.concat(orgFolders)), newValue)}
        onCreateFolder={async (folderName, folderType) => {
          const newFolder = await createNewFolder(folderName, folderType);
          setSelectedFolderId(newFolder.id);
        }}
        onSelectedFolder={onSelectedFolder}
        onSearch={(value) => {
          setSearchTerm(value);
        }}
        showSearch={true}
        title={"Add Recommendations"}
        folderSections={orgName ? [userFoldersSection, orgFoldersSection] : [userFoldersSection]}
        modalButton={
          <Button
            text={
              <>
                <PlusIcon className="mr-2 h-4 w-4" />
                Saved Recommendation
              </>
            }
            variant="negative"
            buttonStyle="border"
          />
        }
        // Mosaic's w-full default pulls this away from Custom Recommendation.
        modalButtonClassName="w-fit"
        footer={{
          addButtonText: "Add Recommendations(s)",
          onAdd: addRecommendations,
          onSaveToFolder: () => setSaveToFolderDialogOpen(true),
          displaySaveToFolderButton: selectedFolderId === AI_GENERATED,
        }}
        panel={
          <RecommendationsBankPanel
            userType={userType}
            addNewButton={() => <></>}
            isLoading={recommendationBankQuery.isFetching}
            key={recommendationsToAdd().length}
            isEmpty={false}
            recommendationsToDisplay={recommendationsToAdd()}
            selection={selection}
            onCreateFolder={() => {}}
            emptyState={{
              header: "Nothing Here Yet",
            }}
          />
        }
      >
        <FolderManagementDialog
          key={userFolders.length + orgFolders.length}
          open={isSaveToFolderDialogOpen}
          onCancelClick={() => {
            reset();
            setSaveToFolderDialogOpen(false);
          }}
          action={FolderManagementAction.SAVE}
          onSaveClick={onSubmit}
        >
          <FormContainer onSubmit={onSubmit}>
            <RegisteredFormSelected
              formKey="folderId"
              required
              formState={formState}
              items={getFolderSelections(userFoldersSection, orgFoldersSection)}
              title={"Select Folder"}
              placeholderText={"Select Folder"}
            />
          </FormContainer>
        </FolderManagementDialog>
        <DeleteFolderDialog
          open={openDeleteFolderDialog}
          folderName={findFolder(deleteFolderId, userFolders.concat(orgFolders)).name}
          contentsLabel="recommendations"
          onCancelClick={() => {
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
          onDeleteClick={() => {
            if (deleteFolderId == selectedFolderId) {
              setSelectedFolderId("");
            }
            deleteFolder(findFolder(deleteFolderId, userFolders.concat(orgFolders)));
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
        />
      </FolderExplorer>
    </>
  );
};
